import { useApi } from '@hero/context';
import { useMutationAPI, useQueryAPI } from '../../utils';
import type {
  UseCurrentUserMetaApi,
  UseCurrentUserMetaUpdateApi,
} from './types';

/**
 * `GET /current-user/meta` — reads the caller's User (with the
 * `meta` block populated). Uses `useApi()` — the method lives on
 * every SDK. See `docs/ai/user-signals.md`.
 *
 * @see Hook API: {@link UseCurrentUserMetaApi}
 */
export const useCurrentUserMetaApi: UseCurrentUserMetaApi = apiConfig => {
  const api = useApi();
  return useQueryAPI({
    entity: 'currentUser',
    apiFn: api.currentUser.getMeta,
    query: undefined,
    apiConfig: { disableIfNotAuth: true, ...apiConfig },
  });
};

/**
 * `PATCH /current-user/meta` — orthogonal partial update into the
 * caller's `User.meta`. Callers typically send one or two sections
 * at a time: `appVersion` / `osVersion` / `permissions` snapshot
 * after an OS prompt, `statedLocation` from an agent tool, channel
 * policy edits from the account screen, or timezone on login.
 *
 * `(app, platform)` are NOT part of the body — they ride on the
 * paired `x-hero-app` + `x-hero-platform` headers the SDK sets on
 * every request (same headers the backend `PresenceInterceptor`
 * uses to bump `lastSeenAt` implicitly). See
 * `docs/ai/user-signals.md` for the payload shape and
 * `libs/frontend/web/hooks/src/current-user/usePresenceHeartbeatManager`
 * for the higher-level manager hook.
 *
 * @see Hook API: {@link UseCurrentUserMetaUpdateApi}
 */
export const useUpdateCurrentUserMetaApi: UseCurrentUserMetaUpdateApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'currentUser',
    apiFn: api.currentUser.updateMeta,
    apiConfig,
  });
};
